import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { newId } from '../core/uuid.js';

export type DiagnosticSource = 'ble' | 'attempt' | 'app';

export interface DiagnosticEvent {
  readonly id: string;
  readonly timestampMs: number;
  readonly isoTimestamp: string;
  readonly attemptId: string | null;
  readonly source: DiagnosticSource;
  readonly type: string;
  readonly payload: unknown;
}

export type DiagnosticInput = Omit<DiagnosticEvent, 'id' | 'timestampMs' | 'isoTimestamp'>;

interface DiagnosticsContextValue {
  readonly events: readonly DiagnosticEvent[];
  pushEvent(input: DiagnosticInput): void;
  clear(): void;
}

const DIAGNOSTICS_KEY = 'soluu-harness:diagnostics:v1';
// Loose cap so localStorage doesn't grow unbounded across long sessions.
// Diagnostic logs are debug aids, not durable artifacts.
const MAX_EVENTS = 500;

const DiagnosticsContext = createContext<DiagnosticsContextValue | null>(null);

function loadInitial(): DiagnosticEvent[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(DIAGNOSTICS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as DiagnosticEvent[];
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

export function DiagnosticsProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<readonly DiagnosticEvent[]>(loadInitial);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(DIAGNOSTICS_KEY, JSON.stringify(events));
    } catch {
      /* quota / private mode — silently degrade */
    }
  }, [events]);

  const pushEvent = useCallback((input: DiagnosticInput) => {
    const ts = Date.now();
    const event: DiagnosticEvent = {
      ...input,
      id: newId(),
      timestampMs: ts,
      isoTimestamp: new Date(ts).toISOString(),
    };
    setEvents((prev) => appendCapped(prev, event, MAX_EVENTS));
  }, []);

  const clear = useCallback(() => setEvents([]), []);

  const value = useMemo<DiagnosticsContextValue>(
    () => ({ events, pushEvent, clear }),
    [events, pushEvent, clear],
  );

  return (
    <DiagnosticsContext.Provider value={value}>{children}</DiagnosticsContext.Provider>
  );
}

export function useDiagnostics(): DiagnosticsContextValue {
  const ctx = useContext(DiagnosticsContext);
  if (!ctx)
    throw new Error('useDiagnostics must be used inside <DiagnosticsProvider>');
  return ctx;
}

export function appendCapped<T>(
  prev: readonly T[],
  next: T,
  max: number,
): readonly T[] {
  const out = [...prev, next];
  return out.length > max ? out.slice(out.length - max) : out;
}

export const __testing = { MAX_EVENTS, DIAGNOSTICS_KEY };
